import {EventEmitter} from '@angular/core';
import {Subscription} from 'rxjs/internal/Subscription';
import {Socket as IoSocket} from 'socket.io-client';

import {Socket} from '../classes/socket';
import {ApiResponse} from '../entities/apiResponse';
import {ApiError} from '../entities/error';
import {Request} from '../entities/request';
import {State} from '../entities/state';
import {
  IClientToServerEvents,
  IServerToClientEvents
} from '../interfaces/socket.interface';

import {EntityArray} from './entityArray';
import {Manager} from 'socket.io-client/build/esm/manager';

/**
 * @ignore
 * @internal
 * */
export abstract class NamespaceSocket<REQUEST extends Request, CLIENTEVENTS extends IClientToServerEvents<REQUEST>, SERVEREVENTS extends IServerToClientEvents> {

  protected requestQueueNew: RequestQueueCollection<REQUEST> = new RequestQueueCollection<REQUEST>();

  protected requestQueue: EntityArray<REQUEST> = new EntityArray<REQUEST>();
  protected requestQueueTimer: number = null;

  /**
   * Time, for how long we will wait for new changed, before send it
   *
   * @internal
   * */
  protected requestQueueTime = 500;
  protected requestQueueEvent = new EventEmitter<ApiResponse>();
  protected requestQueueEventSubs: Subscription[] = [];

  protected socket: IoSocket<
    SERVEREVENTS,
    CLIENTEVENTS
  > = null;

  constructor(manager: Manager, namespace: string, success: () => void, errorCallback: (err: ApiError) => void) {
    this.socket = this.makeSocketNameSpace(manager, `/${namespace}`, () => {
      if (this.socket.connected) {
        success();
      } else {
        errorCallback(new ApiError());
      }
    }, (err) => {
      errorCallback(err);
    })
  }

  public get connected() {
    return this.socket?.connected;
  }

  private makeSocketNameSpace(manager: Manager, namespace: string, success: () => void, errorCallback: (err: ApiError) => void) {
    const socket = new IoSocket<
      SERVEREVENTS,
      CLIENTEVENTS
    >
    (manager, namespace);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    socket.on('connectionReady', (data) => {

      if (data.status) {
        success();
        this.subscribeToDisconnectEvents();
      } else {
        socket.disconnect();

        errorCallback(new ApiError(false, data.message));
      }

      this.offInitialEvents();
    });

    socket.connect()

    return socket;
  }

  private offInitialEvents() {
    this.socket.off('connect');
    this.socket.off('connectionReady');
    this.socket.off('disconnect');
    this.socket.off('connect_error');
  }

  private subscribeToDisconnectEvents() {
    this.socket.on('disconnect', (reason, description) => {
      console.log('disconnect', reason, description);
    });

    this.socket.on('connect_error', (err) => {
      console.log('connect_error', err);
    });
  }

  public disconnect() {
    this.socket.offAny();
    this.socket.offAnyOutgoing();

    this.socket.close()

    this.requestQueueEventSubs.forEach((item) => {
      item.unsubscribe();
    });
    this.requestQueueEventSubs = [];

    this.requestQueue = new EntityArray<REQUEST>();
    clearTimeout(this.requestQueueTimer);
    this.requestQueueTimer = null;
  }

  public sendRequest(request: REQUEST) {
    return new Promise<any>((resolve, reject) => {

      this.addRequestToQueue(request, (response: ApiResponse) => {
// @todo chat move it to callback
        if (response.status) {

          if (response.result.items.length) {
            response.result.items.forEach((item: any) => {
              const searchByClientId = request.isType('create');

              const itemObj = item.message ? item.message : item.group;

              const requestObjId = searchByClientId ? (request as any).clientId : ((request as any).messageId ? (request as any).messageId : (request as any).groupId);
              // if (requestObjId === (searchByClientId ? itemObj.clientId : itemObj.id)) {
                resolve(item);
                return true;
              // }
              //
              // return null;
            });
          }
        } else {
          reject(response);
        }
      });

      this.runRequestQueue();
    });
  }

  private addRequestToQueue(request: REQUEST, callback: (item: any) => void) {
    const existedRequest = this.findRequest(request);

    if (existedRequest && existedRequest.status === 'waiting') {
      if (request.data.state && request.data.state.length) {
        request.data.state.forEach((item: State) => {
          const existedSate = existedRequest.request.data.state.getByField('type', item.type);

          if (!existedSate) {
            existedRequest.request.data.state.push(item)
          }
        })
      }

      if (request.data.content) {
        existedRequest.request.data.content = request.data.content;
      }

      existedRequest.request.type = existedRequest.request.type.concat(request.type.filter((item) => existedRequest.request.type.indexOf(item) < 0))
    } else {
      this.requestQueueNew.add(request, callback);
    }
  }

  protected findRequest(request: REQUEST): RequestQueue<REQUEST> {
    return null;
  }

  private runRequestQueue() {
    if (this.requestQueueTimer === null && this.requestQueueNew.getWaiting().length > 0) {
      this.requestQueueTimer = setTimeout(() => {
        const requestQueue = this.requestQueueNew.getWaiting();

        if (requestQueue.length) {
          requestQueue.forEach((item) => item.status = 'loading')
          const request: REQUEST[] = requestQueue.map((item) => item.request);

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          this.socket.emit('request', {request: new EntityArray<REQUEST>(...request)}, (response: ApiResponse) => {

            requestQueue.forEach((item) => {
              item.callback(response);
              item.status = 'ready'
            })
          });

          this.requestQueueNew.cleanQueue();
        }

        clearTimeout(this.requestQueueTimer);
        this.requestQueueTimer = null;

        // this.runRequestQueue();
      }, this.requestQueueTime)
    }

  }
}

/**
 * @ignore
 * @internal
 * */
export class RequestQueueCollection<REQUEST> extends EntityArray<RequestQueue<REQUEST>> {
  add(request: REQUEST, callback: (item: any) => void) {
    this.push(new RequestQueue(request, callback));
  }

  getWaiting() {
    return this.getAllByField('status', 'waiting');
  }

  cleanQueue() {
    this.removeByField('status', 'ready');
  }
}

/**
 * @ignore
 * @internal
 * */
export class RequestQueue<REQUEST> {
  status: RequestQueueStatus = 'waiting'
  callback: (item: any) => void
  request: REQUEST

  constructor(request: REQUEST, callback: (item: any) => void) {
    this.status = 'waiting';
    this.callback = callback;
    this.request = request;
  }
}

/**
 * Loading status <br>
 * - `waiting`, created and waiting to be requested from server
 * - `loading`, requested from server
 * - `ready`, ready to use
 * - `notFound`, was not found by api call
 * */
export type RequestQueueStatus = 'waiting' | 'loading' | 'ready' | 'notFound';
